import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useApolloClient } from "@apollo/client";

import { useElationBridge } from "../bridge/useElationBridge";
import { trackEvent } from "../telemetry/appInsights";
import { createApolloEhrAdapter, type EhrAdapter } from "./patientSwitch/EhrAdapter";
import {
  elationPatientToContext,
  shouldShowPatientContext,
} from "./patientSwitch/elationPatientToContext";
import { PatientInContextCard } from "./patientSwitch/PatientInContextCard";
import {
  astranaStateFromMatch,
  fallbackNavId,
  isConnectFallbackMatch,
  type EhrMemberMatch,
} from "./graphql/resolveEhrMember";
import { useResolvedMember } from "./patientSwitch/useResolvedMember";
import { compareDemographics } from "./patientSwitch/compareDemographics";
import {
  advanceForegroundTracker,
  hasAutoNavigated,
  initForegroundTracker,
  markAutoNavigated,
  pickXwalkAutoNavTarget,
  resetAutoNav,
  shouldResetFormForChartChange,
  shouldRunInContextAutoSearch,
  syncInContextPatient,
} from "./patientSwitch/autoNav";
import type { SearchFilters } from "./MemberResultsTable";
import { MemberSearchCard } from "./MemberSearchCard";
import type { MemberSearchResult } from "./graphql/memberSearch";
import { useMemberSearch } from "./useMemberSearch";
import { validateMemberSearch } from "./memberSearchValidation";
import { parseDate } from "../components/DatePicker";

const EMPTY_FILTERS: SearchFilters = {
  firstName: "",
  lastName: "",
  mpi: "",
  dob: "",
};

// Search filters (first/last name, MPI, DOB) are HIPAA identifiers, so we
// never persist them. Restoration is in-memory only: react-router
// `location.state` carries them back when the user returns from a member
// profile within the same session. A side-panel close/reopen or tab reload
// intentionally drops the search — losing that UX is preferable to leaving
// PHI on disk.
interface LocationState {
  searchFilters?: SearchFilters;
}

interface MemberSearchPageProps {
  /**
   * Whether Search is the ACTIVE (visible) tab in the keep-alive shell. The
   * shell keeps Search mounted at all times (hidden via CSS while a profile or
   * worklist tab is showing); its auto-nav effects still run, so without this
   * gate the always-mounted Search would fire navigate() for the foreground
   * chart's patient even while the user is on another tab. Only gate the
   * navigation effects — the guard-maintenance effects (foreground tracker,
   * syncInContextPatient) must keep tracking the real chart state regardless.
   * Defaults true — in the non-shell path Search is only mounted when active.
   */
  isActiveTab?: boolean;
}

export default function MemberSearchPage({
  isActiveTab = true,
}: MemberSearchPageProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const fromRouter = (location.state as LocationState | null)?.searchFilters;

  const initialFilters = fromRouter ?? EMPTY_FILTERS;
  const initialSubmitted = fromRouter ?? null;

  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const lastPrefilledIdRef = useRef<string | null>(null);
  const [submitted, setSubmitted] = useState<SearchFilters | null>(initialSubmitted);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Default OFF: in-context resolve prefers ACTIVE enrollments. useResolvedMember
  // automatically falls back to including inactive/termed enrollments only when
  // the active-only resolve finds nobody — so a termed patient still matches
  // without defaulting the whole flow to inactive. The toggle lets the user
  // force-include inactive up front.
  const [showInactive, setShowInactive] = useState(false);
  const [showIneligible, setShowIneligible] = useState(false);

  const { search, results, loading, error, requiresDob } = useMemberSearch();
  const elation = useElationBridge();
  const apolloClient = useApolloClient();
  const ehrAdapterRef = useRef<EhrAdapter>(createApolloEhrAdapter(apolloClient));

  // Sticky patient (rule #1): keep the last *non-null* scrape so the matched
  // view doesn't clear when the user switches to a non-Elation tab or an
  // Elation tab with no chart open. Only a different real patient replaces it.
  const [stickyElationData, setStickyElationData] = useState(elation.data);
  useEffect(() => {
    if (elation.data) setStickyElationData(elation.data);
  }, [elation.data]);

  // capturedAt is taken from the scrape transition; a monotonically increasing
  // counter is sufficient for staleness gating (no Date.now needed for correctness).
  const [elationCaptureSeq, setElationCaptureSeq] = useState(0);
  useEffect(() => {
    setElationCaptureSeq((n) => n + 1);
  }, [stickyElationData]);

  const ehrContext = useMemo(
    () => elationPatientToContext(stickyElationData, elationCaptureSeq, elation.sourceTabActive),
    [stickyElationData, elationCaptureSeq, elation.sourceTabActive],
  );
  const { state: resolution, retry } = useResolvedMember(ehrContext, ehrAdapterRef.current, { includeInactive: showInactive });

  // Navigate to a resolved EHR match (xwalk card or auto-nav). Connect-only
  // fallback matches (synthetic pp: id + astranaId, no Mongo doc) route via the
  // library's astranaId path; real Mongo members route by _id.
  const openResolvedMember = useCallback(
    (member: EhrMemberMatch) => {
      const isFallback = isConnectFallbackMatch(member);
      const navId = isFallback ? fallbackNavId(member) : member._id;
      navigate(`/members/${encodeURIComponent(navId)}`, {
        state: {
          ehrPatientId: ehrContext?.ehrPatientId ?? null,
          ...(isFallback ? { astranaMember: astranaStateFromMatch(member) } : {}),
          // P6d multi-tab shell — carry the name already in hand so the
          // opened/focused patient tab gets a real title immediately instead
          // of the placeholder (see TabbedAppShell.tsx). No-op elsewhere.
          ...(member.name ? { tabTitle: member.name } : {}),
        },
      });
    },
    [navigate, ehrContext?.ehrPatientId],
  );

  // Navigate to a demographic-search result (mirrors MemberResultsTable's
  // routing), carrying the Elation patient id so the profile keeps EHR context.
  const navigateToSearchResult = useCallback(
    (m: MemberSearchResult) => {
      const pid = ehrContext?.ehrPatientId ?? null;
      // P6d multi-tab shell — see openResolvedMember's tabTitle comment above.
      const tabTitle = m.name ?? undefined;
      if (m._id.startsWith("pp:")) {
        navigate(`/members/${encodeURIComponent(m._id)}`, {
          state: { ppMember: m._pp, ehrPatientId: pid, ...(tabTitle ? { tabTitle } : {}) },
        });
      } else {
        navigate(`/members/${m._id}`, {
          state: { ehrPatientId: pid, ...(tabTitle ? { tabTitle } : {}) },
        });
      }
    },
    [navigate, ehrContext?.ehrPatientId],
  );

  // Fix B1: re-arm auto-nav after a genuine tab-away-and-back, WITHOUT
  // re-bouncing on a plain search→profile→back-to-search. The once-per-patient
  // guard is deliberately durable across the search-page remount that navigation
  // causes, so re-viewing the same chart after a real Chrome-tab switch would
  // otherwise never re-navigate. But `useElationBridge` re-initializes
  // `sourceTabActive` to false on every mount, so the bounce ALSO shows a
  // false→true transition — we must not treat that as a refocus. The tell:
  // a genuine tab-away drives a true→false blur while this page stays mounted;
  // the bounce (a fresh mount) never does. advanceForegroundTracker re-arms only
  // on false→true that follows a real true→false. Runs before the auto-nav
  // effects so the guard is cleared in the same commit.
  const foregroundTrackerRef = useRef(
    initForegroundTracker(elation.sourceTabActive),
  );
  useEffect(() => {
    const { tracker, reArm } = advanceForegroundTracker(
      foregroundTrackerRef.current,
      elation.sourceTabActive,
    );
    foregroundTrackerRef.current = tracker;
    if (reArm) resetAutoNav();
  }, [elation.sourceTabActive]);

  // Re-arm auto-nav whenever a DIFFERENT patient becomes the in-context chart.
  // The once-per-patient guard only advances when a patient actually
  // auto-navigates, so visiting an intermediate chart that finds no match (e.g.
  // an eCW patient not in our layer) would otherwise leave the FIRST patient
  // permanently blocked on return — the blur→refocus re-arm doesn't cover it
  // because switching directly between two EHR tabs keeps sourceTabActive true
  // (no true→false blur). syncInContextPatient is driven by the current patient
  // id and clears the module-level guard on any change to a different patient,
  // so it works even when the search page remounts fresh at the new patient
  // (fires on mount too). A same-patient update is a no-op → the
  // search→profile→back-to-search bounce never re-arms → no auto-nav loop.
  useEffect(() => {
    syncInContextPatient(ehrContext?.ehrPatientId ?? null);
  }, [ehrContext?.ehrPatientId]);

  // Req 1: auto-navigate on an ElationId xwalk match. For multiple enrollments,
  // pickXwalkAutoNavTarget selects the single active one; a hard name/DOB
  // mismatch (possible bad xwalk) falls back to the card's confirm gate. Guarded
  // to fire at most once per Elation patient (last-patient guard), and only when
  // the chart is the tab the clinician is actually viewing (sourceTabActive) —
  // never auto-jump to a patient whose chart is only open in a background tab.
  useEffect(() => {
    if (!isActiveTab) return; // keep-alive shell: only the active tab navigates
    const pid = ehrContext?.ehrPatientId;
    if (!pid || !ehrContext || hasAutoNavigated(pid)) return;
    if (!ehrContext.sourceTabActive) return;
    const target = pickXwalkAutoNavTarget(resolution);
    if (!target) return;
    if (compareDemographics(ehrContext, target).kind === "mismatch") return;
    markAutoNavigated(pid);
    openResolvedMember(target);
  }, [resolution, ehrContext, openResolvedMember, isActiveTab]);

  // Distinguishes the demographic AUTO-search (Elation-driven) from a manual
  // search — only the former is eligible for req-2 auto-navigation.
  const lastSearchWasAutoRef = useRef(false);

  // When the in-context Elation patient actually CHANGES (chart switch), drop the
  // previous patient's search fields + results so the form/results never show a
  // different patient than the context card (e.g. a prior manual search or the
  // prior patient's auto-search). Reset to the NEW patient's demographics rather
  // than empty so a manual re-search is one click away. Skipped on initial mount
  // (prev === null) so a router-restored search isn't wiped.
  //
  // Foreground-only (same consistency gate as the auto-search): a chart in a
  // BACKGROUND tab must never reset the clinician's manual search. We return
  // early WITHOUT advancing the ref while not foreground, so the ref tracks only
  // foreground-observed patients — a chart that changed while backgrounded still
  // triggers a reset when it returns to the foreground.
  const prevForegroundPatientIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ehrContext?.sourceTabActive) return;
    const pid = ehrContext?.ehrPatientId ?? null;
    const prev = prevForegroundPatientIdRef.current;
    prevForegroundPatientIdRef.current = pid;
    if (
      !shouldResetFormForChartChange({
        sourceTabActive: ehrContext.sourceTabActive,
        prevInContextPatientId: prev,
        currentInContextPatientId: pid,
      })
    ) {
      return;
    }
    setFilters({
      firstName: ehrContext?.firstName ?? "",
      lastName: ehrContext?.lastName ?? "",
      mpi: "",
      dob: ehrContext?.dob ?? "",
    });
    setSubmitted(null);
    setValidationError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehrContext?.ehrPatientId, ehrContext?.sourceTabActive]);

  // Auto-search on no crosswalk match — run demographic search once per patient
  // id, and ONLY while the chart is the foreground tab. The foreground gate
  // (via shouldRunInContextAutoSearch) is what stops a STICKY background-tab
  // patient from hijacking a clinician's manual search: without it, on a fresh
  // mount this effect re-filled the form + results with the sticky in-context
  // patient and re-ran its search, so a clicked result never stuck and no member
  // profile could be opened. Mirrors the sourceTabActive gate the two auto-nav
  // effects already use; sourceTabActive is a dep so it re-fires when the chart
  // legitimately returns to the foreground.
  const autoSearchedRef = useRef<string | null>(null);
  useEffect(() => {
    const pid = ehrContext?.ehrPatientId ?? null;
    if (
      !shouldRunInContextAutoSearch({
        resolutionKind: resolution.kind,
        sourceTabActive: ehrContext?.sourceTabActive ?? false,
        ehrPatientId: pid,
        alreadySearchedPatientId: autoSearchedRef.current,
        // Needs BOTH names: PathwaysMembersSearch matches on the
        // firstName/lastName pair and rejects a half pair, so a one-sided EHR
        // name is not searchable. A Provider Portal login also needs the
        // chart's DOB as a complete MM/DD/YYYY date — PP returns nothing for a
        // name-only search, and buildIsoDate drops any other shape.
        hasName:
          !!(ehrContext?.firstName && ehrContext?.lastName) &&
          (!requiresDob || parseDate(ehrContext?.dob ?? "") !== null),
      })
    ) {
      return;
    }
    autoSearchedRef.current = pid;
    lastSearchWasAutoRef.current = true;
    const auto = { firstName: ehrContext!.firstName ?? "", lastName: ehrContext!.lastName ?? "", mpi: "", dob: ehrContext!.dob ?? "" };
    setFilters(auto);
    setSubmitted(auto);
    void search({ ...auto, showIneligibleMembers: showIneligible });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution.kind, ehrContext?.ehrPatientId, ehrContext?.sourceTabActive]);

  // Req 2: when the demographic AUTO-search (not a manual one) returns exactly
  // one result that matches the Elation patient's name+DOB, auto-navigate. The
  // auto-search runs with showIneligibleMembers off, so its results are
  // active-only ("only one active result"). Same once-per-patient guard.
  useEffect(() => {
    if (!isActiveTab) return; // keep-alive shell: only the active tab navigates
    const pid = ehrContext?.ehrPatientId;
    if (!pid || !ehrContext || hasAutoNavigated(pid)) return;
    if (!ehrContext.sourceTabActive) return; // only when viewing the chart's tab
    if (!lastSearchWasAutoRef.current) return; // manual searches never auto-nav
    if (loading || showIneligible) return; // wait for results; must be active-only
    if (results.length !== 1) return;
    const only = results[0];
    if (!only._id) return;
    if (compareDemographics(ehrContext, only).kind === "mismatch") return;
    markAutoNavigated(pid);
    navigateToSearchResult(only);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, loading, showIneligible, ehrContext, navigateToSearchResult, isActiveTab]);

  // If we restored a prior search from router state, re-run the query on
  // mount so the results come back.
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    if (!fromRouter) return;
    didRestoreRef.current = true;
    lastSearchWasAutoRef.current = false; // restored manual search — not an auto match
    void search({ ...fromRouter, showIneligibleMembers: showIneligible });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Elation bridge (embedded mode) pre-fill — only once per patient id so
  // subsequent scrape polls don't re-clobber user-edited fields.
  useEffect(() => {
    const pid = elation.data?.ehrPatientId ?? null;
    if (!elation.data || pid === lastPrefilledIdRef.current) return;
    lastPrefilledIdRef.current = pid;
    setFilters((prev) => ({
      firstName: prev.firstName || elation.data?.firstName || "",
      lastName: prev.lastName || elation.data?.lastName || "",
      mpi: prev.mpi,
      dob: prev.dob || elation.data?.dob || "",
    }));
  }, [elation.data]);

  function updateField(key: keyof SearchFilters, value: string) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (validationError) setValidationError(null);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Catch an unsearchable form here so the user sees the real rule rather
    // than a generic backend 400 or an empty result — see memberSearchValidation.
    const invalid = validateMemberSearch(filters, { requiresDob });
    if (invalid) {
      setValidationError(invalid);
      return;
    }
    setValidationError(null);
    lastSearchWasAutoRef.current = false; // manual search — never auto-navigate
    const next = { ...filters };
    setSubmitted(next);
    // Emit before search — no PHI in props (name/MPI are not included).
    trackEvent("member_search_executed");
    void search({ ...filters, showIneligibleMembers: showIneligible });
  }

  function onClear() {
    setFilters(EMPTY_FILTERS);
    setSubmitted(null);
    setValidationError(null);
  }

  return (
    // Home's page frame (`p-6`, `gap-4` between cards), minus its scroll
    // container: in shell mode Search's wrapper owns the scroll, for scroll
    // preservation (see TabbedAppShell).
    <div className="flex flex-col gap-4 p-6">
      {/* Fix B2: hide the "In Elation" card once the clinician has navigated
          away from the chart (it's no longer the foreground tab). Sticky scrape
          data is kept intact, so returning to the foreground chart re-shows the
          card without a re-scrape — see shouldShowPatientContext.

          Above the search card: when a chart is in front, the in-context
          patient is what this page is for, and the auto-search it drives
          fills the card below. */}
      {shouldShowPatientContext(ehrContext) && (
        <PatientInContextCard
          ehrContext={ehrContext}
          state={resolution}
          onOpenMember={openResolvedMember}
          onRetry={retry}
          showInactive={showInactive}
          onShowInactiveChange={setShowInactive}
        />
      )}

      {/* The SAME card as Home's search widget (see MemberSearchCard), with
          the results uncapped: this is the full page. */}
      <MemberSearchCard
        idPrefix="ms"
        filters={filters}
        onFieldChange={updateField}
        showIneligible={showIneligible}
        onShowIneligibleChange={(next) => {
          setShowIneligible(next);
          // Re-run the current search immediately so counts update.
          if (submitted) {
            void search({ ...submitted, showIneligibleMembers: next });
          }
        }}
        onSubmit={onSubmit}
        onClear={onClear}
        loading={loading}
        validationError={validationError}
        error={error}
        submitted={submitted}
        results={results}
        requiresDob={requiresDob}
      />
    </div>
  );
}
